import { useApiClient } from '~/api-client';
import { ElasticSearchResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { orderBy } from 'lodash';
import { TAB_QUERY_CACHE_TIME } from '../tabs/constants';
import { HoistedQueryProps } from './types';

export function useEventLogQuery(props: {
    eventId?: number;
    hoistedQueryProps?: HoistedQueryProps;
}) {
    const { eventId } = props;
    const apiClient = useApiClient();
    return useQuery(
        ['GetUserActionsForEvent', eventId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<ElasticSearchResource.GetUserActionsForEvent>({
                    method: 'GET',
                    url: `elastic_search/user_actions/event/${queryKey[1]}/`,
                    params: {
                        format_values: true,
                    },
                })
                .then((data) => {
                    return data.data.data?.items.flatMap((event) => {
                        // Remove any events w/o an eventLogMessage
                        if (!event.eventLogMessage) {
                            return [];
                        }
                        // When there is a single change view we know exactly
                        // what changed, so a missing displayName is acceptable
                        // (e.g. narrative edits show only old/new text, parity
                        // with the BOLO log). With multiple change views we
                        // still require a displayName to disambiguate which
                        // field each value belongs to.
                        const isSingleChangeView = (event.changeViews?.length ?? 0) === 1;
                        return {
                            ...event,
                            // Sort the changeViews by display name
                            changeViews: orderBy(
                                event.changeViews,
                                ['displayName'],
                                ['asc']
                            ).flatMap((changeView) => {
                                const { displayName, oldValue, newValue } = changeView;
                                const hasValue = oldValue || newValue;
                                if (isSingleChangeView) {
                                    return hasValue ? changeView : [];
                                }
                                if (displayName && hasValue) {
                                    return changeView;
                                }
                                // Drop change views with no value or no display
                                // name when several are present.
                                return [];
                            }),
                        } as typeof event;
                    });
                });
        },
        {
            cacheTime: TAB_QUERY_CACHE_TIME,
            ...props.hoistedQueryProps,
        }
    );
}
